/**
 * Phone number formatting utilities
 */

/**
 * Format a phone number for display
 * Converts +14157549214 or 4157549214 to (415) 754-9214
 */
export const formatPhoneNumber = (value: string): string => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, '');
  
  // Don't format if empty
  if (!phoneNumber) return '';
  
  // Remove leading 1 if present (US country code)
  const localNumber = phoneNumber.startsWith('1') && phoneNumber.length === 11 
    ? phoneNumber.slice(1) 
    : phoneNumber;
  
  // Format based on length
  if (localNumber.length <= 3) {
    return localNumber;
  } else if (localNumber.length <= 6) {
    return `(${localNumber.slice(0, 3)}) ${localNumber.slice(3)}`;
  } else if (localNumber.length <= 10) {
    return `(${localNumber.slice(0, 3)}) ${localNumber.slice(3, 6)}-${localNumber.slice(6)}`;
  } else {
    // For numbers longer than 10 digits, just format the first 10
    return `(${localNumber.slice(0, 3)}) ${localNumber.slice(3, 6)}-${localNumber.slice(6, 10)}`;
  }
};

/**
 * Convert a formatted phone number back to backend format
 * Converts (415) 754-9214 to +14157549214
 */
export const unformatPhoneNumber = (formattedPhone: string): string => {
  // Remove all non-numeric characters
  const digits = formattedPhone.replace(/\D/g, '');
  
  // Don't format if empty
  if (!digits) return '';
  
  // If it's 10 digits, add +1 prefix
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  
  // If it already has country code (11 digits starting with 1), add + prefix
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  
  // If it already starts with +, return as is
  if (formattedPhone.startsWith('+')) {
    return formattedPhone;
  }
  
  // Default: return what we have with + prefix
  return `+${digits}`;
};

export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  // Remove formatting and check if it's a valid US phone number (10 digits)
  const digits = phoneNumber.replace(/\D/g, '');
  return digits.length === 10 || digits.length === 11; // 10 for US, 11 for +1 US
};

export const handlePhoneInput = (
  e: React.ChangeEvent<HTMLInputElement>,
  setFormData: React.Dispatch<React.SetStateAction<any>>,
  fieldName: string
) => {
  const formatted = formatPhoneNumber(e.target.value);
  setFormData((prev: any) => ({
    ...prev,
    [fieldName]: formatted
  }));
};
